
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { ResourceManager } from './ResourceManager';
import { fetchLessonResources } from '@/services/modules/resourceService';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { CheckCircle, Circle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { updateLessonCompletionStatus } from '@/services/modules/lessonService';

interface LessonContentRendererProps {
  content: string;
  lessonId: string;
  isCompleted?: boolean;
  onCompletionUpdate?: (isCompleted: boolean) => void;
}

export const LessonContentRenderer = ({
  content,
  lessonId,
  isCompleted = false,
  onCompletionUpdate
}: LessonContentRendererProps) => {
  const [resources, setResources] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [completionStatus, setCompletionStatus] = useState(isCompleted);
  const [isUpdating, setIsUpdating] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    setCompletionStatus(isCompleted);
  }, [isCompleted]);

  useEffect(() => {
    const loadResources = async () => {
      if (lessonId) {
        setIsLoading(true);
        try {
          const data = await fetchLessonResources(lessonId);
          setResources(data);
        } catch (error) {
          console.error('Error loading resources:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadResources();
  }, [lessonId]);

  const handleCompletionToggle = async () => {
    if (!user || !lessonId) return;
    
    setIsUpdating(true);
    try {
      const newStatus = !completionStatus;
      const success = await updateLessonCompletionStatus(lessonId, newStatus);
      
      if (success) {
        setCompletionStatus(newStatus);
        if (onCompletionUpdate) {
          onCompletionUpdate(newStatus);
        }
        
        toast({
          title: newStatus ? "Lesson marked as completed" : "Lesson marked as incomplete",
          description: newStatus ? "Your progress has been saved." : "Lesson status updated.",
        });
      } else {
        throw new Error("Failed to update lesson status");
      }
    } catch (error) {
      console.error('Error updating lesson completion status:', error);
      toast({
        title: "Error updating lesson status",
        description: "There was a problem updating your progress. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="prose prose-gray dark:prose-invert max-w-none">
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
      
      {user && (
        <div className="flex justify-end">
          <Button 
            variant={completionStatus ? "default" : "outline"}
            onClick={handleCompletionToggle}
            disabled={isUpdating}
            className="flex items-center gap-2"
          >
            {completionStatus ? 
              <CheckCircle className="h-4 w-4" /> : 
              <Circle className="h-4 w-4" />
            }
            {isUpdating ? "Updating..." : completionStatus ? "Completed" : "Mark as Completed"}
          </Button>
        </div>
      )}
      
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : (
            <ResourceManager 
              resources={resources} 
              onChange={() => {}} 
              lessonId={lessonId}
              readOnly={true}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};
